import type { ChangeEvent } from "react";
import { styled } from "styled-components";

import { EmailIcon, Image, UserPlaceholderIcon } from "common/components";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { getUserId } from "domains/authentication/selectors";
import { changePermission, removeInviteMapSharing } from "domains/createShareSettings/actions";
import { AccessListItem } from "domains/createShareSettings/components/AccessListItem";
import type { Collaborator, Invitee } from "domains/createShareSettings/types";
import { activateModal } from "domains/modal/actions";

const StyledImage = styled(Image)`
  width: 34px;
  height: 34px;
  border-radius: 17px;
`;

const StyledLinkContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  min-width: 34px;
  height: 34px;
  border-radius: 17px;
  background-color: ${({ theme }) => theme.colors.linkIconBackground};
`;

const StyledEmailIcon = styled(EmailIcon)`
  width: 15px;
  height: 15px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.backgroundSecondary};
`;

const getIcon = (name: string | null, isPending = false, userImage: string | null) => {
  if (isPending) {
    return (
      <StyledLinkContainer>
        <StyledEmailIcon />
      </StyledLinkContainer>
    );
  }
  return (
    <StyledImage src={userImage ?? undefined} alt={name ?? ""} renderPlaceholder={() => <UserPlaceholderIcon />} />
  );
};

type UserAccessListItemProps = {
  topicId: string;
  user: Collaborator | Invitee;
};

export const UserAccessListItem = ({ user, topicId }: UserAccessListItemProps) => {
  const dispatch = useDispatch();
  const translate = useTranslate();
  const currentUserId = useSelector(getUserId);

  const title = user.pending
    ? translate("app.create.share_settings.invitation_pending")
    : `${user.first_name} ${user.last_name}`;

  const onPermissionChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    if (value !== "editor" && value !== "viewer") return;
    dispatch(changePermission({ email: user.email, permission: value, topic_id: topicId }));
  };

  const removeAction = () => {
    if (user.email === null || user.permission === "owner") return;
    if (user.id === currentUserId)
      dispatch(activateModal({ id: "ConfirmRemoveCollaborator", params: { email: user.email, topicId } }));
    else dispatch(removeInviteMapSharing({ topic_id: topicId, email: user.email }));
  };

  return (
    <AccessListItem
      icon={getIcon(user.email, user.pending, user.image_url)}
      title={title}
      caption={user.email ?? undefined}
      permission={user.permission}
      onPermissionChange={onPermissionChange}
      isPermissionChanging={user.isPermissionChanging}
      removePending={user.removePending}
      removeAction={user.permission === "owner" ? undefined : removeAction}
    />
  );
};
